import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { broadcastAuth, withRefreshLock } from './auth-channel';
import { toApiError } from './errors';
import { tokenStore } from './token-store';
import type { ApiEnvelope, AuthSession } from './types';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const http = axios.create({
  baseURL: API_BASE_URL,
  // Sends the refresh cookie to /auth/*; the cookie itself is path-scoped there.
  withCredentials: true,
  timeout: 20_000,
});

// Calls that must never trigger the refresh-and-retry dance. List exact public endpoints:
// a broad prefix like '/users/invitations/' would also strip the token from resend/revoke.
const NO_REFRESH = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/users/invitations/validate',
  '/users/invitations/complete',
];
const isNoRefresh = (url = '') => NO_REFRESH.some((path) => url === path || url.startsWith(`${path}?`));

// A token with less than this left is refreshed instead of used.
const MIN_TOKEN_LIFETIME_MS = 30_000;

http.interceptors.request.use((config) => {
  const token = tokenStore.getAccessToken();
  if (token && !isNoRefresh(config.url)) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

let refreshInFlight: Promise<AuthSession> | null = null;

/**
 * Gets a fresh access token from the refresh cookie. Concurrent callers share
 * one request, and across tabs only one refresh runs at a time. A tab that
 * waited for the lock reuses the token another tab obtained meanwhile.
 */
export const refreshSession = (): Promise<AuthSession> => {
  refreshInFlight ??= (async () => {
    const tokenBefore = tokenStore.getAccessToken();
    return withRefreshLock(async () => {
      const current = tokenStore.getAccessToken();
      const user = tokenStore.getUser();
      const refreshedMeanwhile = current !== null && current !== tokenBefore;
      if (refreshedMeanwhile && user && tokenStore.remainingMs() > MIN_TOKEN_LIFETIME_MS) {
        return { accessToken: current, expiresIn: Math.floor(tokenStore.remainingMs() / 1000), user };
      }
      const { data } = await http.post<ApiEnvelope<AuthSession>>('/auth/refresh');
      tokenStore.set(data.data);
      broadcastAuth({ type: 'session', session: data.data, issuedAt: Date.now() });
      return data.data;
    });
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const apiError = toApiError(error);
    const config = axios.isAxiosError(error) ? (error.config as RetriableConfig | undefined) : undefined;

    if (apiError.status === 401 && config && !config._retried && !isNoRefresh(config.url)) {
      config._retried = true;
      const sentWith = config.headers.get('Authorization');
      const current = tokenStore.getAccessToken();
      // Another request (or tab) already refreshed: just retry with the new token.
      if (!current || sentWith === `Bearer ${current}`) {
        try {
          await refreshSession();
        } catch {
          tokenStore.clear('expired');
          throw apiError;
        }
      }
      return http(config);
    }
    throw apiError;
  },
);

/** Typed helpers that unwrap the `{ data }` envelope. */
export const api = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return (await http.get<ApiEnvelope<T>>(url, config)).data.data;
  },
  async post<T = void>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return (await http.post<ApiEnvelope<T>>(url, body, config)).data.data;
  },
  async patch<T = void>(url: string, body?: unknown): Promise<T> {
    return (await http.patch<ApiEnvelope<T>>(url, body)).data.data;
  },
  async delete<T = void>(url: string): Promise<T> {
    return (await http.delete<ApiEnvelope<T>>(url)).data.data;
  },
};
